import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import store from '@/store';
import RootTypes from '@/store/types/root';
import authType from '@/store/types/auth';
import router from '@/router';
import { ObjectKeysGeneric } from '@/store/interfaces/generic';
const URL: string | undefined = process.env.VUE_APP_URL_API;

const http = axios.create({
  baseURL: `${URL}/api/v1/`,
});

let refreshPromise: Promise<void> | null = null;

http.interceptors.response.use(undefined, function (err) {
  return new Promise(function (resolve, reject) {
    const type = err.response?.data?.type;
    const skiped404 = [
      'accounts/confirmations',
      'accounts/passwords',
      'accounts/registrations',
      'accounts/unlocks',
      'accounts/passwords/setup',
      'companies/company/customer_profiles/handles'
    ];
    const errorUrl = err.response?.config?.url ?? '';
    if (err.response?.status === 404 && !skiped404.includes(errorUrl)) {
      router.push({ name: 'NotFound', }, resolve, reject);
      throw type;
    }
    if (err.response?.status === 401) {
      if (type === 'locked_account_by_attempts') {
        throw type;
      }
      const isOAuthTokenRequest = errorUrl === 'oauth/token' || (typeof errorUrl === 'string' && errorUrl.includes('oauth/token'));
      if (isOAuthTokenRequest) {
        reject(err);
        return;
      }
      const isAuthenticated = store.getters['authModule/' + authType.getters.IS_AUTHENTICATED];
      if (!isAuthenticated) {
        store.dispatch('authModule/' + authType.actions.AUTH_LOGOUT).then(() => {
          router.push({ name: 'Login', });
        });
        reject(type);
        return;
      }
      const originalConfig = err.config;
      const alreadyRetried = (originalConfig as AxiosRequestConfig & { __retried?: boolean }).__retried === true;
      if (alreadyRetried) {
        store.dispatch('authModule/' + authType.actions.AUTH_LOGOUT).then(() => {
          router.push({ name: 'Login', });
        });
        reject(type);
        return;
      }
      const doRefreshAndRetry = (): Promise<void> => {
        if (refreshPromise) {
          return refreshPromise;
        }
        refreshPromise = store.dispatch('authModule/' + authType.actions.REFRESH_TOKEN)
          .then(() => { })
          .finally(() => {
            refreshPromise = null;
          });
        return refreshPromise;
      };
      doRefreshAndRetry()
        .then(() => {
          (originalConfig as AxiosRequestConfig & { __retried?: boolean }).__retried = true;
          return http.request(originalConfig);
        })
        .then((response: AxiosResponse) => {
          resolve(response);
        })
        .catch(() => {
          // REFRESH_TOKEN already dispatched AUTH_LOGOUT on failure; only redirect
          router.push({ name: 'Login', });
          reject(type);
        });
      return;
    }
    reject(err);
  });
});
// Add a request interceptor
http.interceptors.request.use(
  async config => {
    const url = config.url?.split('?');
    if (url?.length === 2) {
      const obj: ObjectKeysGeneric<string> = {};
      url[1].replace(/([^=&]+)=([^&]*)/g, function(m, key, value): string {
        obj[key] = encodeURIComponent(value);
        return m;
      });
      const queryString: string = Object.entries(obj).map(([key, value]) => `${key}=${value}`).join('&');
      config.url = `${url[0]}?${queryString}`;
    }
    store.commit(RootTypes.mutations.SET_START_PROCESS);
    return config;
  },
  error => {
    // Do something with request error
    store.commit(RootTypes.mutations.SET_END_PROCESS);
    return Promise.reject(error);
  }
);

// Add a response interceptor
http.interceptors.response.use(
  (response: AxiosResponse) => {
    // Do something with response data
    store.commit(RootTypes.mutations.SET_END_PROCESS);
    return response;
  },
  error => {
    // Do something with response error
    store.commit(RootTypes.mutations.SET_END_PROCESS);
    return Promise.reject(error);
  }
);

const token = localStorage.getItem('_token');
if (token) {
  http.defaults.headers.common.Authorization = `Bearer ${token}`;
  const company = store.getters['authModule/' + authType.getters.GET_COMPANY];
  if (company) {
    http.defaults.headers.common['X-COMPANY-ID'] = company;
  }
}

export { http, URL };
